import React, { useState } from 'react';
import {
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Stack
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { ContainerForm } from 'components/shared/container_form';
import { PageTitle } from 'components/shared/page_title';
import { getCPFFormatado, getCNPJFormatado, validarCPF, validarCNPJ, getMascaraMoeda, getFloat } from 'utils/uteis';
import { useNavigate, useLocation } from 'react-router-dom';
import { ROUTES } from 'routes/constants';

interface Produto {
  id: string;
  nomeRazao: string;
  documento: string;
  valor: string;
  valorNumerico: number;
}

interface SecaoAquisicao {
  titulo: string;
  produtosCnpj: Produto[];
  produtosCpf: Produto[];
}

interface Step1Props {
  initialData: SecaoAquisicao[];
  onNext: (secoes: SecaoAquisicao[]) => void;
}

const Step1: React.FC<Step1Props> = ({ initialData, onNext }) => {
  const [secoes, setSecoes] = useState<SecaoAquisicao[]>(initialData);
  const [novosProdutos, setNovosProdutos] = useState([
    { cnpj: { nomeRazao: '', documento: '', valor: '', valorNumerico: 0 }, cpf: { nomeRazao: '', documento: '', valor: '', valorNumerico: 0 } },
    { cnpj: { nomeRazao: '', documento: '', valor: '', valorNumerico: 0 }, cpf: { nomeRazao: '', documento: '', valor: '', valorNumerico: 0 } },
    { cnpj: { nomeRazao: '', documento: '', valor: '', valorNumerico: 0 }, cpf: { nomeRazao: '', documento: '', valor: '', valorNumerico: 0 } }
  ]);

  const navigate = useNavigate();
  const location = useLocation();
  const match = /cifa-(\d{4})/.exec(location.pathname);
  const anoReferencia = match ? Number(match[1]) : 2026;

  // Função para gerar key única para seções
  const getSecaoKey = (secao: SecaoAquisicao, index: number) => {
    return secao.titulo.replace(/\s+/g, '-').toLowerCase() || `secao-${index}`;
  };

  const formatarValorInput = (valor: string) => {
    const apenasDigitos = valor.replace(/\D/g, '');
    if (apenasDigitos === '') return '';
    return getMascaraMoeda(parseInt(apenasDigitos, 10) / 100);
  };

  const handleAddProduto = (secaoIndex: number, tipo: 'cnpj' | 'cpf') => {
    const produto = novosProdutos[secaoIndex][tipo];
    const isValid = tipo === 'cnpj' ? validarCNPJ(produto.documento) : validarCPF(produto.documento);

    if (produto.nomeRazao && isValid && produto.valorNumerico > 0) {
      const newProduto = {
        id: Math.random().toString(36).substr(2, 9),
        nomeRazao: produto.nomeRazao.trim(),
        documento: produto.documento,
        valor: produto.valor,
        valorNumerico: produto.valorNumerico
      };

      setSecoes(prev => {
        const updated = [...prev];
        updated[secaoIndex][`produtos${tipo === 'cnpj' ? 'Cnpj' : 'Cpf'}`] = [
          ...updated[secaoIndex][`produtos${tipo === 'cnpj' ? 'Cnpj' : 'Cpf'}`],
          newProduto
        ];
        return updated;
      });

      handleClearFields(secaoIndex, tipo);
    }
  };

  const handleClearFields = (secaoIndex: number, tipo: 'cnpj' | 'cpf') => {
    setNovosProdutos(prev => {
      const updated = [...prev];
      updated[secaoIndex][tipo] = { nomeRazao: '', documento: '', valor: '', valorNumerico: 0 };
      return updated;
    });
  };

  const handleInputChange = (secaoIndex: number, tipo: 'cnpj' | 'cpf', field: string, value: string) => {
    setNovosProdutos(prev => {
      const updated = [...prev];

      if (field === 'documento') {
        const formattedValue = tipo === 'cnpj'
          ? getCNPJFormatado(value.replace(/\D/g, ''))
          : getCPFFormatado(value.replace(/\D/g, ''));

        updated[secaoIndex][tipo] = {
          ...updated[secaoIndex][tipo],
          documento: formattedValue
        };
      } else if (field === 'valor') {
        const valorFormatado = formatarValorInput(value);
        const valorNumerico = getFloat(valorFormatado);

        updated[secaoIndex][tipo] = {
          ...updated[secaoIndex][tipo],
          valor: valorFormatado,
          valorNumerico
        };
      } else {
        updated[secaoIndex][tipo] = {
          ...updated[secaoIndex][tipo],
          [field]: value
        };
      }

      return updated;
    });
  };

  const isFormValid = () => secoes.some(secao =>
    secao.produtosCnpj.length > 0 || secao.produtosCpf.length > 0
  );

  const handleRemoveProdutoCnpj = (secaoIndex: number, produtoId: string) => {
    const newSecoes = [...secoes];
    newSecoes[secaoIndex].produtosCnpj = newSecoes[secaoIndex].produtosCnpj.filter(p => p.id !== produtoId);
    setSecoes(newSecoes);
  };

  const handleRemoveProdutoCpf = (secaoIndex: number, produtoId: string) => {
    const newSecoes = [...secoes];
    newSecoes[secaoIndex].produtosCpf = newSecoes[secaoIndex].produtosCpf.filter(p => p.id !== produtoId);
    setSecoes(newSecoes);
  };

  const handleBack = () => {
    navigate(ROUTES[`CIFA${anoReferencia}` as keyof typeof ROUTES] as string);
  };

  return (
    <>
      <PageTitle
        title="Aquisição de alimentação escolar"
        subtitle="Detalhamento dos valores aplicados para aquisição de alimentação escolar"
      />

      <Stack marginX="4rem" spacing="2rem" maxWidth="1152px" width="100%">
        {secoes.map((secao, secaoIndex) => (
          <ContainerForm key={getSecaoKey(secao, secaoIndex)} title={secao.titulo}>
            {/* Seção CNPJ */}
            <Typography variant="h6" sx={{ mb: 2 }}>
              {secaoIndex + 1}.1 Valores via CNPJ
            </Typography>

            <TableContainer component={Paper} sx={{ mb: 3 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Nome/Razão Social</TableCell>
                    <TableCell>CNPJ</TableCell>
                    <TableCell>Valor</TableCell>
                    <TableCell align="right" width="250px">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        value={novosProdutos[secaoIndex].cnpj.nomeRazao}
                        onChange={(e) => handleInputChange(secaoIndex, 'cnpj', 'nomeRazao', e.target.value)}
                        placeholder="Digite o nome/razão social"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        value={novosProdutos[secaoIndex].cnpj.documento}
                        onChange={(e) => handleInputChange(secaoIndex, 'cnpj', 'documento', e.target.value)}
                        placeholder="00.000.000/0000-00"
                        inputProps={{ maxLength: 18 }}
                        error={!!novosProdutos[secaoIndex].cnpj.documento && !validarCNPJ(novosProdutos[secaoIndex].cnpj.documento)}
                        helperText={!!novosProdutos[secaoIndex].cnpj.documento && !validarCNPJ(novosProdutos[secaoIndex].cnpj.documento) ? "CNPJ inválido" : ""}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        value={novosProdutos[secaoIndex].cnpj.valor}
                        onChange={(e) => handleInputChange(secaoIndex, 'cnpj', 'valor', e.target.value)}
                        placeholder="0,00"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleClearFields(secaoIndex, 'cnpj')}
                          disabled={
                            !novosProdutos[secaoIndex].cnpj.nomeRazao &&
                            !novosProdutos[secaoIndex].cnpj.documento &&
                            !novosProdutos[secaoIndex].cnpj.valor
                          }
                        >
                          Limpar
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleAddProduto(secaoIndex, 'cnpj')}
                          disabled={
                            !novosProdutos[secaoIndex].cnpj.nomeRazao ||
                            !validarCNPJ(novosProdutos[secaoIndex].cnpj.documento) ||
                            novosProdutos[secaoIndex].cnpj.valorNumerico <= 0
                          }
                        >
                          Adicionar
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                  {secao.produtosCnpj.map((produto) => (
                    <TableRow key={produto.id}>
                      <TableCell>{produto.nomeRazao}</TableCell>
                      <TableCell>{produto.documento}</TableCell>
                      <TableCell>{produto.valor}</TableCell>
                      <TableCell align="right">
                        <IconButton onClick={() => handleRemoveProdutoCnpj(secaoIndex, produto.id)}>
                          <DeleteIcon color="error" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Seção CPF */}
            <Typography variant="h6" sx={{ mb: 2 }}>
              {secaoIndex + 1}.2 Valores via CPF
            </Typography>

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Nome/Razão Social</TableCell>
                    <TableCell>CPF</TableCell>
                    <TableCell>Valor</TableCell>
                    <TableCell align="right" width="250px">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        value={novosProdutos[secaoIndex].cpf.nomeRazao}
                        onChange={(e) => handleInputChange(secaoIndex, 'cpf', 'nomeRazao', e.target.value)}
                        placeholder="Digite o nome/razão social"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        value={novosProdutos[secaoIndex].cpf.documento}
                        onChange={(e) => handleInputChange(secaoIndex, 'cpf', 'documento', e.target.value)}
                        placeholder="000.000.000-00"
                        inputProps={{ maxLength: 14 }}
                        error={!!novosProdutos[secaoIndex].cpf.documento && !validarCPF(novosProdutos[secaoIndex].cpf.documento)}
                        helperText={!!novosProdutos[secaoIndex].cpf.documento && !validarCPF(novosProdutos[secaoIndex].cpf.documento) ? "CPF inválido" : ""}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        value={novosProdutos[secaoIndex].cpf.valor}
                        onChange={(e) => handleInputChange(secaoIndex, 'cpf', 'valor', e.target.value)}
                        placeholder="0,00"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleClearFields(secaoIndex, 'cpf')}
                          disabled={
                            !novosProdutos[secaoIndex].cpf.nomeRazao &&
                            !novosProdutos[secaoIndex].cpf.documento &&
                            !novosProdutos[secaoIndex].cpf.valor
                          }
                        >
                          Limpar
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleAddProduto(secaoIndex, 'cpf')}
                          disabled={
                            !novosProdutos[secaoIndex].cpf.nomeRazao ||
                            !validarCPF(novosProdutos[secaoIndex].cpf.documento) ||
                            novosProdutos[secaoIndex].cpf.valorNumerico <= 0
                          }
                        >
                          Adicionar
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                  {secao.produtosCpf.map((produto) => (
                    <TableRow key={produto.id}>
                      <TableCell>{produto.nomeRazao}</TableCell>
                      <TableCell>{produto.documento}</TableCell>
                      <TableCell>{produto.valor}</TableCell>
                      <TableCell align="right">
                        <IconButton onClick={() => handleRemoveProdutoCpf(secaoIndex, produto.id)}>
                          <DeleteIcon color="error" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </ContainerForm>
        ))}

        <Stack direction="row" spacing={2} justifyContent="center" mt={4}>
          <Button variant="outlined" onClick={handleBack}>
            Voltar
          </Button>
          <Button
            variant="contained"
            onClick={() => onNext(secoes)}
            disabled={!isFormValid()}
          >
            Avançar
          </Button>
        </Stack>
      </Stack>
    </>
  );
};

export default Step1;
